/**
 * Dashboard API adapter for fetching user statistics
 */

import { logger } from '../logging';

export interface DashboardStats {
  totalComparisons: number;
  monthlyTokenUsage: number;
  monthlyTokenLimit: number;
  monthlyTokenCostUsd?: number;
  avgScore: number | null;
  favoriteModel: string | null;
}

/**
 * Fetches dashboard statistics for the authenticated user
 * @returns Dashboard stats including comparisons count, average score, and favorite model
 */
export async function fetchDashboardStats(): Promise<DashboardStats> {
  const apiUrl = import.meta.env.VITE_API_BASE_URL || '';
  const url = `${apiUrl}/api/dashboard-stats`;

  try {
    logger.debug('Fetching dashboard stats', { url });

    // Get Firebase auth token
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch dashboard stats: ${response.statusText}`
      );
    }

    const data: DashboardStats = await response.json();

    logger.info('Dashboard stats fetched successfully', {
      totalComparisons: data.totalComparisons,
      monthlyTokenUsage: data.monthlyTokenUsage,
      monthlyTokenLimit: data.monthlyTokenLimit,
      hasAvgScore: data.avgScore !== null,
      hasFavoriteModel: data.favoriteModel !== null,
    });

    return data;

  } catch (error) {
    logger.error('Failed to fetch dashboard stats', {
      error: error instanceof Error ? error.message : 'Unknown error',
      url,
    });
    throw error;
  }
}
